// Back where you were after the till locks.
//
// The till locks after a minute idle, often mid-order, and unlocking always landed on Tables:
// a server half way through a round had to find their table again. Now the lock remembers
// who was where; the PIN screen says so ("Sarah was on T1 · 2 items not sent yet"), and that
// person's PIN goes straight back. Anyone else's lands where it always did, and the unsent
// items stay on T1 for whoever comes back to it (the order draft keeps them).
//
// Kept on this device only, and only for half an hour: a lock left overnight is not a place
// anybody is coming back to.

const KEY = 'verlo_lock_resume';
export const RESUME_FOR_MS = 30 * 60 * 1000;

// What the screen a person is on wants said about it. The order page sets this as its table
// and cart change; everything else is named from its path.
let current = null;
export function setWhere(where) { current = where; }
export function clearWhere() { current = null; }

const NAMED = [
  [/^\/kitchen/, 'the kitchen board'],
  [/^\/bar-board/, 'the bar board'],
  [/^\/bar\b/, 'the bar'],
  [/^\/orders/, 'Orders'],
  [/^\/bookings/, 'Bookings'],
  [/^\/till/, 'the till'],
];

/** What to remember when the till locks, or null when there is nowhere worth going back to. */
export function whereFor(path, where = current) {
  const bare = (path || '').split('?')[0];
  if (!bare || bare === '/tables' || bare === '/' || bare.startsWith('/kiosk')) return null;
  if (where && where.path === bare) return { path, label: where.label, unsent: where.unsent || 0 };
  const named = NAMED.find(([re]) => re.test(bare));
  return named ? { path, label: named[1], unsent: 0 } : null;
}

const userIdOf = (user) => user?.id || user?.user_id || null;

export function remember(user, path, storage = globalThis.localStorage, now = Date.now()) {
  const where = whereFor(path);
  const id = userIdOf(user);
  try {
    if (!where || !id) { storage?.removeItem(KEY); return null; }
    const entry = { userId: id, name: (user.name || '').split(' ')[0], ...where, at: now };
    storage?.setItem(KEY, JSON.stringify(entry));
    return entry;
  } catch { return null; }
}

/** The remembered place, if it is still fresh. */
export function peek(storage = globalThis.localStorage, now = Date.now()) {
  try {
    const entry = JSON.parse(storage?.getItem(KEY) || 'null');
    if (!entry || now - entry.at > RESUME_FOR_MS) return null;
    return entry;
  } catch { return null; }
}

/** Where `user` should land: back where they were, if it was them; else null. Taking it
 * forgets it, so a second unlock goes to the landing page as normal. */
export function take(user, storage = globalThis.localStorage, now = Date.now()) {
  const entry = peek(storage, now);
  if (!entry || entry.userId !== userIdOf(user)) return null;
  try { storage?.removeItem(KEY); } catch { /* storage unavailable: nothing to forget */ }
  return entry.path;
}

/** Forget any remembered place: a person who locks the till themselves has finished, and
 *  "Priya was on T4" on the PIN pad would be news to nobody (Oct 2026 sweep). */
export function forget(storage = globalThis.localStorage) {
  try { storage?.removeItem(KEY); } catch { /* storage unavailable: nothing to forget */ }
}
